import { ChangeDetectionStrategy, Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { FieldInterventionBloc } from '@connect-services/client/blocs/field-intervention.bloc';
import { AbstractLayerProjectComponent } from '@connect-services/client/components/abstract-layer-project/abstract-layer-project.component';
import {
    EditFieldInterventionContextLayersModalComponent,
    EditFieldInterventionContextLayersModalData,
} from '@connect-services/client/components/edit-field-intervention-context-layers-modal/edit-field-intervention-context-layers-modal.component';
import { ExportModalComponent } from '@connect-services/client/components/export-modal/export-modal.component';
import { ExportModalReturnType } from '@connect-services/client/components/export-modal/export-modal.model';
import { FieldInterventionInformationComponent } from '@connect-services/client/components/field-intervention-information/field-intervention-information.component';
import {
    DashboardInterventionEntityByOperator,
    EntitiesDashboardInformationsByOperator,
    EntitiesDashboardInformationsByStatus,
    EntitiesDashboardInformationTypeEnum,
    FieldInterventionOperatorOrStatusMetricsComponent,
} from '@connect-services/client/components/field-intervention-information/field-intervention-operator-or-status-metrics/field-intervention-operator-or-status-metrics.component';
import { FieldInterventionTitleComponent } from '@connect-services/client/components/field-intervention-title/field-intervention-title.component';
import { MapComponent } from '@connect-services/client/components/map/map.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { GaugeChartComponent } from '@connect-services/client/components/statistics/gauge-chart/gauge-chart.component';
import { ValueChartComponent } from '@connect-services/client/components/statistics/value-chart/value-chart.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { NotificationEventEnum } from '@connect-services/client/models/notification.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { EntitiesTableByLayerService } from '@connect-services/client/services/entities-table-by-layer.service';
import {
    FieldInterventionContextLayerOption,
    FieldInterventionService,
} from '@connect-services/client/services/field-intervention.service';
import {
    FieldInterventionAreaSelectionContextEnum,
    FieldInterventionAreaSelectionModeService,
} from '@connect-services/client/services/field-intervention-area-selection-mode.service';
import { FieldInterventionMapModeService } from '@connect-services/client/services/field-intervention-map-mode.service';
import { FieldInterventionMapRendererService } from '@connect-services/client/services/field-intervention-map-renderer.service';
import { FieldInterventionPermissionService } from '@connect-services/client/services/field-intervention-permission.service';
import { MapMaskService } from '@connect-services/client/services/map/mask/map-mask.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { NotificationService } from '@connect-services/client/services/notification.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import {
    ComputedStatistic,
    ExportStatus,
    FieldInterventionEntity,
    FieldInterventionsService,
    LayerWithAttributes,
} from '@connect-services/src/sdk/api';
import FieldInterventionExportNotification from '@connect-services/src/sdk/async/FieldInterventionExportNotification';
import JobStatus from '@connect-services/src/sdk/async/JobStatus';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export class DashboardIntervention {
    public constructor(
        public readonly interventionEntitiesByStatus?: FieldInterventionEntity[],
        public readonly interventionEntitiesByOperators?: DashboardInterventionEntityByOperator[],
        public readonly statistics?: ComputedStatistic[],
    ) {}
}

export enum DashboardInterventionStatisticEnum {
    ENTITY = 'entity',
    FORM = 'form',
    INTERVENTION_PROGRESS = 'interventionProgress',
    LAYER = 'layer',
}

const ALL_LAYERS_SELECTED = 'allLayersSelected';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        FieldInterventionInformationComponent,
        FieldInterventionOperatorOrStatusMetricsComponent,
        FieldInterventionTitleComponent,
        GaugeChartComponent,
        LoadingComponent,
        NoDataToDisplayComponent,
        RessourcesModule,
        ValueChartComponent,
    ],
    selector: 'app-field-intervention-dashboard',
    styleUrl: './field-intervention-dashboard.component.scss',
    templateUrl: './field-intervention-dashboard.component.html',
})
export class FieldInterventionDashboardComponent extends AbstractLayerProjectComponent implements OnInit {
    public ALL_LAYERS_SELECTED = 'allLayersSelected';
    public readonly completeIntervention: WritableSignal<DashboardIntervention> = signal<DashboardIntervention>({});
    public readonly computedStatisticHalfGauge: WritableSignal<ComputedStatistic | null> =
        signal<ComputedStatistic | null>(null);
    public readonly contextLayersOptions = signal<FieldInterventionContextLayerOption[]>([]);
    public readonly contextLayersSelected = signal<FieldInterventionContextLayerOption[]>([]);
    public readonly description: WritableSignal<string> = signal('');
    public readonly entitiesStatistics: WritableSignal<ComputedStatistic | null> = signal<ComputedStatistic | null>(
        null,
    );
    public readonly fieldInterventionEntities: WritableSignal<EntitiesDashboardInformationsByStatus> =
        signal<EntitiesDashboardInformationsByStatus>({
            entities: [],
            title: 'entitiesByStatus',
            type: EntitiesDashboardInformationTypeEnum.BY_STATUS,
        });
    public readonly fieldInterventionOperators: WritableSignal<EntitiesDashboardInformationsByOperator> =
        signal<EntitiesDashboardInformationsByOperator>({
            entities: [],
            title: 'entitiesByOperator',
            type: EntitiesDashboardInformationTypeEnum.BY_OPERATOR,
        });
    public readonly formsStatistics: WritableSignal<ComputedStatistic | null> = signal<ComputedStatistic | null>(null);
    public readonly isError: WritableSignal<boolean> = signal(false);
    public readonly isExportJobRunning: WritableSignal<boolean> = signal(false);
    public readonly isLoading: WritableSignal<boolean> = signal(true);
    public readonly layerList: WritableSignal<LayerWithAttributes[] | null> = signal(null);
    public readonly layersStatistics: WritableSignal<ComputedStatistic | null> = signal<ComputedStatistic | null>(null);
    public readonly title: WritableSignal<string> = signal('');

    protected readonly ButtonType = ButtonType;
    protected readonly fieldInterventionBloc = inject(FieldInterventionBloc);
    protected readonly fieldInterventionPermissionService = inject(FieldInterventionPermissionService);
    protected readonly fieldInterventionService = inject(FieldInterventionService);
    protected readonly RoutePathEnum = RoutePathEnum;
    protected readonly sdkFieldInterventionsService = inject(FieldInterventionsService);

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly entitiesTableByLayerService = inject(EntitiesTableByLayerService);
    private readonly fieldInterventionAreaSelectionModeService = inject(FieldInterventionAreaSelectionModeService);
    private readonly fieldInterventionMapModeService = inject(FieldInterventionMapModeService);
    private readonly fieldInterventionMapRendererService = inject(FieldInterventionMapRendererService);
    private readonly mapMaskService = inject(MapMaskService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly matDialog = inject(MatDialog);
    private readonly notificationService = inject(NotificationService<FieldInterventionExportNotification>);
    private readonly snackbarService = inject(SnackBarService);

    public constructor() {
        super();

        this.destroyRef.onDestroy(() => {
            this.fieldInterventionMapModeService.leaveFieldInterventionMapMode();
            this.mapMaskService.clearMasks();

            this.fieldInterventionService.resetHasEntities();
            this.mapSourceService.clearSources(MapComponent.backgroundSource(), []);
        });
    }

    public async ngOnInit(): Promise<void> {
        this.fieldInterventionMapModeService.enterFieldInterventionEntitiesMode();

        await this.checkExportRunningJob();
        await this.initDashboardInformation();
    }

    public openEditContextLayersModal(): void {
        if (!this.contextLayersOptions().length) {
            return;
        }

        const dialogRef = this.matDialog.open(EditFieldInterventionContextLayersModalComponent, {
            autoFocus: false,
            data: {
                options: this.contextLayersOptions(),
                selectedOptions: this.contextLayersSelected(),
            } satisfies EditFieldInterventionContextLayersModalData,
            width: '40vw',
        });

        dialogRef
            .afterClosed()
            .pipe()
            .subscribe((result: FieldInterventionContextLayerOption[]) => {
                if (!result) {
                    this.contextLayersSelected.set([...this.contextLayersSelected()]);

                    return;
                }
                void this.updateContextLayers(result);
            });
    }

    public openExportModal(): void {
        const dialogRef = this.matDialog.open(ExportModalComponent, {});
        dialogRef.afterClosed().subscribe(async (result: ExportModalReturnType): Promise<void> => {
            const { type, ...exportParams } = result;
            if (type === ModalResultEnum.CANCEL) {
                return;
            }

            await this.startExport(exportParams);
        });
    }

    public openFieldInterventionAreaEdition(): void {
        if (!this.fieldInterventionPermissionService.canManageFieldIntervention()) {
            return;
        }

        this.fieldInterventionAreaSelectionModeService.open(FieldInterventionAreaSelectionContextEnum.DASHBOARD);
    }

    public async openFilteredTable(routePath: RoutePathEnum): Promise<void> {
        await this.router.navigate([`../${routePath}`], {
            relativeTo: this.activatedRoute,
        });
    }

    public async startExport(exportParams: Omit<ExportModalReturnType, 'type'>): Promise<void> {
        try {
            this.runExportLoading();

            await this.fieldInterventionService.export(this.fieldInterventionBloc.value()!.id, exportParams);
        } catch {
            this.stopExportLoading();
            this.snackbarService.openErrorSnackBar('exportFailed');
        }
    }

    protected getDashboardInformation(): Promise<DashboardIntervention> {
        return firstValueFrom(
            this.sdkFieldInterventionsService.fieldInterventionControllerGetDashboardStatistics({
                id: this.fieldInterventionBloc.value()!.id,
            }),
        );
    }

    protected async setDashboardInformation(
        fieldInterventionDashboard: DashboardIntervention,
        currentLayerIds: string[],
    ): Promise<void> {
        this.setDashboardStatistics(fieldInterventionDashboard);
        this.setDashboardMetrics(fieldInterventionDashboard);

        await this.entitiesTableByLayerService.setCurrentSources(this.fieldInterventionBloc.value()!, currentLayerIds);
        await this.fieldInterventionMapRendererService.drawSourcesWithAreaMask();

        this.isLoading.set(false);
    }

    protected async updateLayer(event: MatSelectChange): Promise<void> {
        const eventValueConverted = event.toString();

        let currentDashBoardIntervention: DashboardIntervention = this.completeIntervention();
        let currentLayerIds = this.layerList()?.map((layer) => layer.id) ?? [];

        if (eventValueConverted !== ALL_LAYERS_SELECTED) {
            currentDashBoardIntervention = await firstValueFrom(
                this.sdkFieldInterventionsService.fieldInterventionControllerGetDashboardStatistics({
                    id: this.fieldInterventionBloc.value()!.id,
                    layerId: eventValueConverted,
                }),
            );

            currentLayerIds = [eventValueConverted];
        }

        await this.setDashboardInformation(currentDashBoardIntervention, currentLayerIds);
    }

    private async checkExportRunningJob(): Promise<void> {
        const fieldInterventionId = this.fieldInterventionBloc.value()?.id;
        if (!fieldInterventionId) {
            return;
        }

        const exportStatus = await firstValueFrom(
            this.sdkFieldInterventionsService.fieldInterventionControllerGetFieldInterventionExportStatus({
                id: fieldInterventionId,
            }),
        );
        if (exportStatus.status !== ExportStatus.StatusEnum.RUNNING) {
            return;
        }

        this.isExportJobRunning.set(true);
    }

    private async initDashboardInformation(): Promise<void> {
        await this.fieldInterventionService.setHasEntities(this.fieldInterventionBloc.value()!);
        this.title.set(this.fieldInterventionBloc.value()!.name);
        this.description.set(this.fieldInterventionBloc.value()!.description ?? '');

        await this.setLayerList();
        await this.setContextLayersData();

        const fieldInterventionDashboard = await this.getDashboardInformation();

        if (
            !fieldInterventionDashboard?.statistics ||
            !fieldInterventionDashboard.interventionEntitiesByStatus ||
            !fieldInterventionDashboard.interventionEntitiesByOperators
        ) {
            this.isError.set(true);

            return;
        }

        this.completeIntervention.set(fieldInterventionDashboard);

        const currentLayerIds = this.layerList()?.map((layer: LayerWithAttributes) => layer.id) ?? [];

        await this.setDashboardInformation(fieldInterventionDashboard, currentLayerIds);
    }

    private runExportLoading(): void {
        this.notificationService.requestPermission();
        this.notificationService.on(
            NotificationEventEnum.FIELD_INTERVENTION_EXPORT,
            this.updateLoadingState.bind(this),
        );

        this.isExportJobRunning.set(true);
    }

    private async setContextLayersData(): Promise<void> {
        const { options, selectedOptions } = await this.fieldInterventionService.fetchContextLayerSelectionData();

        this.contextLayersOptions.set(options);
        this.contextLayersSelected.set(selectedOptions);
    }

    private setDashboardMetrics(fieldInterventionDashboard: DashboardIntervention): void {
        this.fieldInterventionEntities.set({
            ...this.fieldInterventionEntities(),
            entities: fieldInterventionDashboard!.interventionEntitiesByStatus!,
        });
        this.fieldInterventionOperators.set({
            ...this.fieldInterventionOperators(),
            entities: fieldInterventionDashboard!.interventionEntitiesByOperators!.toSorted(
                (a: DashboardInterventionEntityByOperator, b: DashboardInterventionEntityByOperator) =>
                    (b.percentage ?? 0) - (a.percentage ?? 0),
            ),
        });
    }

    private setDashboardStatistics(fieldInterventionDashboard: DashboardIntervention): void {
        this.computedStatisticHalfGauge.set(
            fieldInterventionDashboard!.statistics!.find(
                (statistic) => statistic.render === ComputedStatistic.RenderEnum.HALF_GAUGE,
            )!,
        );

        this.entitiesStatistics.set(
            fieldInterventionDashboard!.statistics!.find(
                (statistic) =>
                    statistic.render === ComputedStatistic.RenderEnum.VALUE &&
                    statistic.name === DashboardInterventionStatisticEnum.ENTITY,
            )!,
        );

        this.layersStatistics.set(
            fieldInterventionDashboard!.statistics!.find(
                (statistic) =>
                    statistic.render === ComputedStatistic.RenderEnum.VALUE &&
                    statistic.name === DashboardInterventionStatisticEnum.LAYER,
            )!,
        );

        this.formsStatistics.set(
            fieldInterventionDashboard!.statistics!.find(
                (statistic) =>
                    statistic.render === ComputedStatistic.RenderEnum.VALUE &&
                    statistic.name === DashboardInterventionStatisticEnum.FORM,
            )!,
        );
    }

    private async setLayerList(): Promise<void> {
        const layers: LayerWithAttributes[] = await this.fieldInterventionService.fetchAllLayersWithAttributes();

        if (!layers) {
            this.isError.set(true);

            return;
        }

        this.layerList.set(layers);
    }

    private stopExportLoading(): void {
        this.notificationService.off(
            NotificationEventEnum.FIELD_INTERVENTION_EXPORT,
            this.updateLoadingState.bind(this),
        );
        this.isExportJobRunning.set(false);
    }

    private async updateContextLayers(selectedOptions: FieldInterventionContextLayerOption[]): Promise<void> {
        const selectedLayerIds = this.fieldInterventionService.mapContextLayerOptionsToLayerIds(selectedOptions);

        try {
            await this.fieldInterventionService.updateFieldInterventionContextLayers(selectedLayerIds);

            this.snackbarService.openSuccessSnackBar('fieldInterventionEditionSuccess');
        } catch {
            this.snackbarService.openErrorSnackBar('errorPATCH');
        } finally {
            await this.setContextLayersData();
        }
    }

    private updateLoadingState(data: unknown): void {
        AssertService.assertFieldInterventionExportNotification(data);

        if (this.fieldInterventionBloc.value()!.id !== data.fieldInterventionId) {
            return;
        }

        if (data.jobStatus !== JobStatus.RUNNING) {
            this.stopExportLoading();
        }

        if (data.jobStatus === JobStatus.FAILED) {
            this.snackbarService.openErrorSnackBar('exportFailed');
        }

        if (data.jobStatus === JobStatus.SUCCESS) {
            this.snackbarService.openSuccessSnackBar('exportFinishedWithEmailDownload');
        }
    }
}
